import { Col, Modal, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import TableComponent from 'components/atoms/TableComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import ErrModal from 'components/molecules/SpecialniSluzby/ErrModal';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { IZaevidovaniGet, ueZaevidovaniGet, ueZaevidovaniPost } from 'services/specialniSluzby/ueZaevidovani';

import routePathEnum from 'enums/routePathEnum';
import { ETypPozadavku } from 'enums/specialniSluzby/typPozadavku';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

const SeznamPozadavkuZaevidovani: React.FC = () => {
    const { aktivniFirma, specialniSluzby, language } = useContext(Context);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataLze, setDataLze] = useState<IZaevidovaniGet>();
    const [dataNelze, setDataNelze] = useState<IZaevidovaniGet>();
    const [errModalMsg, setErrModalMsg] = useState<string>();
    const [paginationNelze, setPaginationNelze] = useState<{
        offset: number;
        page: number;
        limit: number;
    }>({
        offset: 0,
        page: 1,
        limit: 10
    });
    const [paginationLze, setPaginationLze] = useState<{
        offset: number;
        page: number;
        limit: number;
    }>({
        offset: 0,
        page: 1,
        limit: 10
    });
    const [modalMessage, setModalMessage] = useState<string>('');

    const loadDataFunc = (lze: boolean = true, offset?: number) => {
        if (specialniSluzby?.detail && specialniSluzby.seznamTyp === ETypSeznam.ZAEVIDOVANY) {
            setLoading(true);
            return ueZaevidovaniGet(specialniSluzby?.detail?.id!, {
                idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                ucet: specialniSluzby.detailFiltr?.ucet,
                idnabyvatele: specialniSluzby.detailFiltr?.idnabyvatele,
                koducastnika: specialniSluzby.detailFiltr?.koducastnika,
                zdrojdat: specialniSluzby.detailFiltr?.zdrojdat,
                idvcdcp: specialniSluzby.detailFiltr?.idvcdcp,
                typtransakce:
                    specialniSluzby.detail.typ === ETypPozadavku.SICAV
                        ? specialniSluzby.detailFiltr?.typtransakce
                        : undefined,
                stav: specialniSluzby.detailFiltr?.stav,
                evidovat: lze,
                offset: offset !== undefined ? offset : lze ? paginationLze.offset : paginationNelze.offset,
                limit: lze ? paginationLze.limit : paginationNelze.limit
            });
        }
    };

    const loadDataBoth = () => {
        if (specialniSluzby?.detail && specialniSluzby.seznamTyp === ETypSeznam.ZAEVIDOVANY) {
            setLoading(true);
            const promisses = [];

            promisses.push(
                new Promise(resolve => {
                    const resProm = loadDataFunc(true);
                    if (resProm)
                        resProm
                            .then((resp: IZaevidovaniGet) => {
                                setDataLze(resp);
                            })
                            .catch((err: any) => {
                                setErrStatus(err.response?.status);
                            })
                            .finally(() => {
                                resolve(true);
                            });
                })
            );

            promisses.push(
                new Promise(resolve => {
                    const resProm = loadDataFunc(false);
                    if (resProm)
                        resProm
                            .then((resp: IZaevidovaniGet) => {
                                setDataNelze(resp);
                            })
                            .catch((err: any) => {
                                setErrStatus(err.response?.status);
                            })
                            .finally(() => {
                                resolve(true);
                            });
                })
            );

            Promise.all(promisses).then(() => {
                setLoading(false);
                setErrStatus(0);
            });
        }
    };

    const columns = [
        {
            title: <TranslateText>ISIN</TranslateText>,
            dataIndex: 'isin',
            key: 'isin',
            render: () => specialniSluzby?.detail?.isin
        },
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            key: 'idnabyvatele'
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            dataIndex: 'koducastnika',
            key: 'koducastnika'
        },
        {
            title: <TranslateText>CISLO_ZASTAVNI_SMLOUVY</TranslateText>,
            dataIndex: 'cislozastavnismlouvy',
            key: 'cislozastavnismlouvy'
        },
        {
            title: <TranslateText>POZNAMKA</TranslateText>,
            dataIndex: 'poznamka',
            key: 'poznamka'
        },
        {
            title: <TranslateText>ZPUSOB_PORIZENI</TranslateText>,
            dataIndex: 'zpusobporizeni',
            key: 'zpusobporizeni',
            render: (text: string) => {
                return <TranslateText>{'SS_DETAIL_TAB_PORIZENI_' + text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        }
    ];

    const columnsSICAV = [
        {
            title: <TranslateText>ISIN</TranslateText>,
            dataIndex: 'isin',
            key: 'isin',
            render: () => specialniSluzby?.detail?.isin
        },
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>ID_NABYVATELE</TranslateText>,
            dataIndex: 'idnabyvatele',
            key: 'idnabyvatele'
        },
        {
            title: <TranslateText>SS_DETAIL_FILTR_TYP</TranslateText>,
            dataIndex: 'typtransakce',
            key: 'typtransakce',
            render: (text: string) =>
                text && (
                    <TranslateText>
                        {text === 'NAKUP' ? 'SS_DETAIL_FILTR_NAKUP' : text === 'PRODEJ' ? 'SS_DETAIL_FILTR_PRODEJ' : ''}
                    </TranslateText>
                )
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            dataIndex: 'koducastnika',
            key: 'koducastnika'
        },
        {
            title: <TranslateText>POZNAMKA</TranslateText>,
            dataIndex: 'poznamka',
            key: 'poznamka'
        },
        {
            title: <TranslateText>ZPUSOB_PORIZENI</TranslateText>,
            dataIndex: 'zpusobporizeni',
            key: 'zpusobporizeni',
            render: (text: string) => {
                return <TranslateText>{'SS_DETAIL_TAB_PORIZENI_' + text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        }
    ];

    useEffect(() => {
        if (specialniSluzby) {
            specialniSluzby.detail?.id && loadDataBoth();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialniSluzby?.detail?.id]);

    const renderModal = (msg: string) => {
        setModalMessage(msg);
    };

    const renderButtons = () => {
        return (
            <Row className="button-row">
                <ButtonComponent
                    id="seznamPozadavkuZpetBtn"
                    buttontext="ZPET"
                    onClick={() => {
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`);
                    }}
                />
                {specialniSluzby && (
                    <ButtonComponent
                        id="seznamPozadavkuSubmitBtn"
                        buttontext="ODESLAT"
                        onClick={() => {
                            setLoading(true);
                            ueZaevidovaniPost(specialniSluzby.detail?.id!, {
                                idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                                ucet: specialniSluzby.detailFiltr?.ucet,
                                idnabyvatele: specialniSluzby.detailFiltr?.idnabyvatele,
                                koducastnika: specialniSluzby.detailFiltr?.koducastnika,
                                zdrojdat: specialniSluzby.detailFiltr?.zdrojdat
                            })
                                .then(resp => {
                                    resp.stav?.includes('TIMEOUT')
                                        ? setErrModalMsg('SS_SLUZBA_ERROR_NESKORO')
                                        : renderModal('SS_SEZNAM_MODAL_USPESNE_' + specialniSluzby.seznamTyp);
                                })
                                .catch((err: any) => {
                                    // TODO: doplnit stavy pro response uložení podobně jako v NovyRucni.tsx?
                                    renderModal('RESPONSE_ERROR_' + err.response?.status);
                                })
                                .finally(() => {
                                    setLoading(false);
                                });
                        }}
                    />
                )}
            </Row>
        );
    };

    return (
        <>
            <LoadingWrapper loading={loading}>
                <Title
                    level={4}
                    text={`${getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]} - ${
                        getLanguageObject(language).ISIN
                    }: ${specialniSluzby?.detail?.isin}, ${getLanguageObject(language).DATUM}: ${dayjs(
                        specialniSluzby?.detail?.datum
                    ).format('DD.MM.YYYY')}`}
                />
                {errStatus ? (
                    <Row>
                        <Col span={16}>
                            {errStatus === 404 ? (
                                <Paragraph text="RESPONSE_ERROR_404" />
                            ) : errStatus ? (
                                <Paragraph text="SLUZBA_NEDOSTUPNA" />
                            ) : null}
                            <Row className="button-row">
                                <ButtonComponent
                                    id="seznamPozadavkuErrBtn"
                                    buttontext="ZPET"
                                    onClick={() => {
                                        setErrStatus(0);
                                    }}
                                />
                            </Row>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row>
                            <InputComponent
                                name="pocetchybnych"
                                label="SS_NOVY_SOUBOR_POCET_CHYBNYCH"
                                notRequired={true}
                                defaultValue={dataNelze?.count || 0}
                                disabled={true}
                            />
                            <InputComponent
                                name="pocetbezchybnych"
                                label="SS_NOVY_SOUBOR_POCET_BEZCHYBNYCH"
                                notRequired={true}
                                defaultValue={dataLze?.count || 0}
                                disabled={true}
                            />
                        </Row>

                        <Title level={4} text="SS_SEZNAM_ZAEVIDOVAT_NELZE" />
                        <TableComponent
                            rowKey={(_, index) => index || 0}
                            columns={specialniSluzby?.detail?.typ === 'SICAV' ? columnsSICAV : columns || []}
                            dataSource={dataNelze?.polozky || []}
                            locale={{
                                emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                            }}
                            pagination={{
                                total: dataNelze?.count,
                                onChange: page => {
                                    setPaginationNelze({
                                        page: page,
                                        offset: (page - 1) * paginationNelze.limit,
                                        limit: paginationNelze.limit
                                    });

                                    const fn = loadDataFunc(false, (page - 1) * paginationNelze.limit);
                                    if (fn) {
                                        setLoading(true);
                                        fn.then((resp: IZaevidovaniGet) => {
                                            setDataNelze(resp);
                                        })
                                            .catch((err: any) => {
                                                setErrStatus(err.response?.status);
                                            })
                                            .finally(() => {
                                                setLoading(false);
                                            });
                                    }
                                },
                                current: paginationNelze.page,
                                showSizeChanger: false
                            }}
                        />
                        {dataNelze?.polozky.length && (
                            <Row
                                style={{
                                    paddingTop: '10px'
                                }}
                                gutter={8}
                            >
                                <Col>
                                    <InputComponent
                                        name="pocetcpnelze"
                                        label="POCET_CP"
                                        notRequired={true}
                                        defaultValue={formatInputNumberThousand(dataNelze.pocetcp || 0)}
                                        disabled={true}
                                    />
                                </Col>
                                <Col>
                                    <InputComponent
                                        name="pocetcp"
                                        label="SS_PREHLED_POCET_UCTU"
                                        notRequired={true}
                                        defaultValue={formatInputNumberThousand(dataNelze.pocetuctu || 0)}
                                        disabled={true}
                                    />
                                </Col>
                            </Row>
                        )}

                        <Title
                            style={{
                                paddingTop: '20px'
                            }}
                            level={4}
                            text="SS_SEZNAM_ZAEVIDOVAT_LZE"
                        />
                        <TableComponent
                            rowKey={(_, index) => index || 0}
                            columns={specialniSluzby?.detail?.typ === 'SICAV' ? columnsSICAV : columns || []}
                            dataSource={dataLze?.polozky || []}
                            locale={{
                                emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                            }}
                            pagination={{
                                total: dataLze?.count,
                                onChange: page => {
                                    setPaginationLze({
                                        page: page,
                                        offset: (page - 1) * paginationLze.limit,
                                        limit: paginationLze.limit
                                    });
                                    const fn = loadDataFunc(true, (page - 1) * paginationLze.limit);
                                    if (fn) {
                                        setLoading(true);
                                        fn.then((resp: IZaevidovaniGet) => {
                                            setDataLze(resp);
                                        })
                                            .catch((err: any) => {
                                                setErrStatus(err.response?.status);
                                            })
                                            .finally(() => {
                                                setLoading(false);
                                            });
                                    }
                                },
                                current: paginationLze.page,
                                showSizeChanger: false
                            }}
                        />

                        {dataLze?.polozky.length && (
                            <Row
                                style={{
                                    paddingTop: '10px'
                                }}
                                gutter={8}
                            >
                                <Col>
                                    <InputComponent
                                        name="pocetcplze"
                                        label="POCET_CP"
                                        notRequired={true}
                                        defaultValue={formatInputNumberThousand(dataLze.pocetcp || 0)}
                                        disabled={true}
                                    />
                                </Col>
                                <Col>
                                    <InputComponent
                                        name="pocetcp"
                                        label="SS_PREHLED_POCET_UCTU"
                                        notRequired={true}
                                        defaultValue={formatInputNumberThousand(dataLze.pocetuctu || 0)}
                                        disabled={true}
                                    />
                                </Col>
                            </Row>
                        )}

                        {renderButtons()}
                    </>
                )}
            </LoadingWrapper>
            <ErrModal modalMsg={errModalMsg} setModalMsg={setErrModalMsg} />
            <Modal
                title={
                    <TranslateText>
                        {modalMessage.includes('ERROR_')
                            ? 'CHYBA'
                            : specialniSluzby?.seznamTyp === ETypSeznam.SMAZANY
                              ? 'SMAZANI_POZADAVKU'
                              : specialniSluzby?.seznamTyp === ETypSeznam.OVERENY
                                ? 'SS_OVERENI_UCTU'
                                : 'SS_ZAEVIDOVAT_V_CDCP'}
                    </TranslateText>
                }
                open={!!modalMessage}
                onCancel={() => {
                    setModalMessage('');
                    !modalMessage.includes('ERROR_') &&
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`);
                }}
                afterClose={() => {
                    setModalMessage('');
                    !modalMessage.includes('ERROR_') &&
                        navigateWorkaround(`/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`);
                }}
                footer={null}
            >
                <Row>
                    <div>
                        {!modalMessage.includes('ERROR_') && (
                            <TranslateText>
                                {specialniSluzby?.seznamTyp === 'SMAZANY'
                                    ? 'SS_SEZNAM_MODAL_USPESNE_POZADAVKY'
                                    : 'SS_SEZNAM_MODAL_USPESNE'}
                            </TranslateText>
                        )}{' '}
                        <TranslateText>{modalMessage}</TranslateText>
                    </div>
                    {!modalMessage.includes('ERROR_') && (
                        <div>
                            <Paragraph text={'SS_SEZNAM_MODAL_ZAEVIDOVAT_TEXT'} />
                        </div>
                    )}
                </Row>
                <Row>
                    <ButtonComponent
                        id="seznamPozadavkuZpetAltBtn"
                        style={{
                            marginTop: 20
                        }}
                        buttontext="ZAVRIT"
                        type="default"
                        onClick={() => {
                            setModalMessage('');
                            !modalMessage.includes('ERROR_') &&
                                navigateWorkaround(
                                    `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU}`
                                );
                        }}
                    />
                </Row>
            </Modal>
        </>
    );
};

export default SeznamPozadavkuZaevidovani;
